import {
  BoundDateField,
  BoundTextAreaField,
  Button,
  Css,
  FormLines,
  ModalBody,
  ModalFooter,
  ModalHeader,
  useModal,
  useSnackbar,
  useTestIds,
} from "@homebound/beam";
import { ObjectConfig, required, useFormState } from "@homebound/form-state";
import { format } from "date-fns";
import { Observer } from "mobx-react";
import { useCallback } from "react";
import {
  SaveTradePartnerAvailabilityRequestInput,
  SchedulesPage_TaskFragment,
  TradePartnerAvailabilityRequestStatus,
  useSaveTradePartnerAvailabilityRequestMutation,
  useUpdateTradePartnerAvailabilityRequestStatusMutation,
} from "src/generated/graphql-types";
import { DateOnly } from "src/utils";
import { ConfirmationActionType } from "./SchedulesPage";

interface ConfirmOrRescheduleModalProps {
  selectedTasks: SchedulesPage_TaskFragment[];
  type: ConfirmationActionType;
}

export function ConfirmOrRescheduleModal({ selectedTasks, type }: ConfirmOrRescheduleModalProps) {
  const { closeModal } = useModal();
  const { triggerNotice } = useSnackbar();
  const [updateTradePartnerAvailabilityRequestStatus] = useUpdateTradePartnerAvailabilityRequestStatusMutation({});
  const [saveTradePartnerAvailabilityRequest] = useSaveTradePartnerAvailabilityRequestMutation({});
  const tids = useTestIds({}, "scheduleConfirmationModal");
  const formState = useFormState({
    config: formConfig,
  });
  const isConfirmType = type === "Confirm";

  const onSubmit = useCallback(async () => {
    isConfirmType
      ? await updateTradePartnerAvailabilityRequestStatus({
          variables: {
            input: {
              ids: selectedTasks.flatMap(
                ({ tradePartnerAvailabilityRequests }) => tradePartnerAvailabilityRequests?.map(({ id }) => id) ?? [],
              ),
              status: TradePartnerAvailabilityRequestStatus.Confirmed,
            },
          },
          onCompleted: () => {
            triggerNotice({
              message: `You marked ${selectedTasks.length} task as confirmed`,
            });
          },
          refetchQueries: ["SchedulesPage"],
        })
      : await saveTradePartnerAvailabilityRequest({
          variables: {
            input: {
              id: selectedTasks.first?.tradePartnerAvailabilityRequests?.first?.id ?? "",
              status: TradePartnerAvailabilityRequestStatus.RescheduleNeeded,
              rescheduleDates: mapToInput(formState.value.rescheduleDates),
              rescheduleReasonNote: formState.value.rescheduleReasonNote,
            },
          },
          onCompleted: () => {
            triggerNotice({
              message: `You requested 1 task for a reschedule`,
            });
          },
          refetchQueries: ["SchedulesPage"],
        });
    closeModal();
  }, [
    selectedTasks,
    closeModal,
    updateTradePartnerAvailabilityRequestStatus,
    triggerNotice,
    isConfirmType,
    saveTradePartnerAvailabilityRequest,
    formState.value,
  ]);

  return (
    <>
      <ModalHeader>{isConfirmType ? "Confirm" : "Reschedule"}</ModalHeader>
      <ModalBody>
        <FormLines>
          {isConfirmType ? (
            "Are you sure you want to confirm your availability for this task(s)?"
          ) : (
            <div css={Css.gray700.sm.$}>
              Are you sure you want to reschedule this task{" "}
              <span css={Css.gray900.smSb.$}>{`'${selectedTasks.first?.name}'`}</span>? You may provide new availability
              by selecting a date below.
              <p css={Css.pt2.$}>
                <span css={Css.gray900.smSb.$}>{"Note this date is not confirmed."}</span> You will receive an email
                follow up to finalize this date if the Superintendent accepts it.
              </p>
            </div>
          )}
          {selectedTasks.map(({ id, interval }) => (
            <div css={Css.df.jcsb.mt3.$} key={id}>
              {isConfirmType && (
                <span css={Css.gray700.sm.$} {...tids.taskDates}>{`${format(interval.startDate, "dd MMM")} - ${format(
                  interval.endDate,
                  "dd MMM",
                )}`}</span>
              )}
            </div>
          ))}
          {type === "Reschedule" && (
            <>
              <BoundDateField
                label="Select Rescheduled Date*"
                field={formState.rescheduleDates}
                disabledDays={[{ before: new Date() }]}
              />
              <BoundTextAreaField field={formState.rescheduleReasonNote} label="Reason for Date Change*" />
            </>
          )}
        </FormLines>
      </ModalBody>
      <ModalFooter>
        <Button variant="tertiary" label="Cancel" onClick={closeModal} />
        <Observer>
          {() => (
            <Button
              label={isConfirmType ? "Confirm" : "Reschedule"}
              disabled={isConfirmType ? false : !formState.valid}
              onClick={onSubmit}
            />
          )}
        </Observer>
      </ModalFooter>
    </>
  );
}

type FormValue = Omit<SaveTradePartnerAvailabilityRequestInput, "rescheduleDates"> & {
  rescheduleDates: Date | null | undefined;
};

const formConfig: ObjectConfig<FormValue> = {
  rescheduleDates: { type: "value", rules: [required] },
  rescheduleReasonNote: { type: "value", rules: [required] },
};

function mapToInput(rescheduleDates: Date | null | undefined): DateOnly[] {
  return rescheduleDates ? [new DateOnly(rescheduleDates)] : [];
}
